import React, {
    Dispatch,
    ReactElement,
    Ref,
    SetStateAction,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import {
    ColumnDef,
    RowSelectionState,
    Table,
    Updater,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import scssVariables from 'styles/variables.module.scss';

import { TUseReactTableState } from 'hooks/useReactTableState';

import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { Header } from 'components/common/Tables/TableV8/components/Header/Header';
import { Row } from 'components/common/Tables/TableV8/components/Row/Row';
import classes from 'components/common/Tables/TableV8/tableV8.module.scss';
import { Pagination } from 'components/common/pagination/customPagination';

import { PaginationWithPageSize } from 'components/DesignSystem/Pagination/PaginationWithPageSize';

const { mainTableMaxHeight } = scssVariables;

const noDataSize = 200;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE_INDEX = 0;
const TABLE_EXTRA_GAP = 2;
const PAGINATION_HEIGHT_SIZE = 65;

interface Props<TData> extends Partial<TUseReactTableState> {
    columns: ColumnDef<TData, unknown>[];
    data: TData[];
    defaultColumn?: Partial<ColumnDef<TData>>;
    noData?: ReactElement;
    isLoading?: boolean;
    maxHeight?: number;
    manualSorting?: boolean;
    sortDescFirst?: boolean;
    manualPagination?: boolean;
    manualFiltering?: boolean;
    rowSelection: RowSelectionState;
    setRowSelection: (updater: Updater<RowSelectionState>) => void;
    tableClassName?: string;
    wrapperClassName?: string;
    tableHeaderClassName?: string;
    rowClassName?: string;
    totalPages: number;
    pagination?: Pagination;
    setPagination?: Dispatch<SetStateAction<Pagination>>;
    isPaginationDisabled?: boolean;
    displayPaginationBottomBar?: boolean;
    showPageSizeOptions?: boolean;
}

export interface TableRef<TData> {
    table: Table<TData>;
}

export const TableV8 = forwardRef(
    <TData,>(
        {
            columns,
            data,
            defaultColumn = { enableColumnFilter: false, enableGrouping: false, size: 150, minSize: 0 },
            noData = <NoData />,
            isLoading = false,
            maxHeight = parseInt(mainTableMaxHeight),
            sorting,
            setSorting,
            columnFilters,
            setColumnFilters,
            columnVisibility,
            setColumnVisibility,
            columnOrder,
            setColumnOrder,
            manualSorting = true,
            sortDescFirst = false,
            manualPagination = true,
            manualFiltering = true,
            pagination,
            rowSelection = {},
            setRowSelection = () => {},
            tableClassName,
            wrapperClassName,
            tableHeaderClassName,
            rowClassName,
            totalPages,
            setPagination,
            isPaginationDisabled,
            displayPaginationBottomBar = false,
            showPageSizeOptions = true,
        }: Props<TData>,
        parentRef: Ref<TableRef<TData>>,
    ) => {
        const ref = useRef<HTMLDivElement>(null);
        const table = useReactTable({
            data,
            columns,
            defaultColumn: { ...defaultColumn },
            filterFns: {},
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            sortDescFirst,
            state: {
                sorting,
                columnFilters,
                columnVisibility,
                columnOrder,
                rowSelection,
                pagination: {
                    pageSize: pagination?.size ?? DEFAULT_PAGE_SIZE,
                    pageIndex: pagination?.page ? pagination?.page - 1 : DEFAULT_PAGE_INDEX,
                },
            },
            onSortingChange: setSorting,
            onColumnFiltersChange: setColumnFilters,
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnOrder,
            getPaginationRowModel: getPaginationRowModel(),
            manualSorting,
            manualPagination,
            manualFiltering,
            onRowSelectionChange: setRowSelection,
        });

        // Expose the table instance to the parent
        useImperativeHandle(parentRef, () => ({
            table,
        }));

        const { rows } = table.getRowModel();
        const tableContainerRef = React.useRef<HTMLDivElement>(null);

        const [tableWidth, setTableWidth] = useState<number | string>(0);
        const [tableHeight, setTableHeight] = useState<number | string>(0);

        useEffect(() => {
            if (ref.current) {
                const totalWidth = table.getTotalSize();
                setTableWidth(totalWidth > ref.current.clientWidth ? totalWidth : '100%');
            }
        }, [table.getTotalSize()]);

        useEffect(() => {
            if (tableContainerRef.current && !isLoading) {
                const clientHeight = tableContainerRef.current.clientHeight;
                const currentHeight = data.length === 0 ? noDataSize : Math.min(clientHeight, maxHeight);
                setTableHeight(currentHeight + TABLE_EXTRA_GAP);
            }
        }, [data, isLoading, pagination?.size, columnFilters]);

        useEffect(() => {
            const resizeHandler = () => {
                if (ref.current) {
                    const totalWidth = table.getTotalSize();
                    setTableWidth(totalWidth > ref.current.clientWidth ? totalWidth : '100%');
                }
                if (tableContainerRef.current) {
                    const clientHeight = tableContainerRef.current.clientHeight;
                    const currentHeight = Math.min(clientHeight, maxHeight);
                    setTableHeight(currentHeight + TABLE_EXTRA_GAP);
                }
            };
            addEventListener('resize', resizeHandler);
            return () => {
                removeEventListener('resize', resizeHandler);
            };
        }, []);

        return (
            <div className={`${classes.MainTable} ${wrapperClassName ? wrapperClassName : ''}`}>
                <div
                    ref={ref}
                    className={`${classes.container} ${tableClassName ? tableClassName : ''} `}
                    style={{
                        overflow: 'auto',
                        position: 'relative',
                        height: tableHeight,
                        contain: 'strict',
                        minHeight: '150px',
                    }}
                >
                    <div
                        ref={tableContainerRef}
                        style={{
                            width: tableWidth,
                            position: 'relative',
                        }}
                    >
                        <div className={classes.sticky}>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Header
                                    key={headerGroup.id}
                                    headerGroup={headerGroup}
                                    className={tableHeaderClassName}
                                />
                            ))}
                        </div>
                        <div>
                            {rows.map((row) => {
                                return <Row key={row.id} row={row} rowClassName={rowClassName} />;
                            })}
                        </div>
                    </div>
                    {!isLoading && rows.length === 0 && <div className={classes.noDataWrapper}>{noData}</div>}
                    {isLoading && <OverlayWithSpinner />}
                </div>
                {displayPaginationBottomBar && pagination && setPagination && totalPages > 0 && (
                    <div className={classes.pagination}>
                        <PaginationWithPageSize
                            totalPages={totalPages}
                            pagination={pagination}
                            setPagination={setPagination}
                            isDisabled={isPaginationDisabled}
                            showPageSizeOptions={showPageSizeOptions}
                        />
                    </div>
                )}
            </div>
        );
    },
);
